import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { fbTrack } from '../lib/consent';
import {
  ArrowRight,
  ArrowLeft,
  Calculator,
  TrendingUp,
  Shield,
  Mail,
  CheckCircle,
  Lock,
  Sparkles,
  Building2,
  Briefcase,
  Stethoscope,
  Star,
  Zap,
  PiggyBank,
  AlertTriangle,
  BarChart3,
  FileText,
  Gift,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Checkout URLs — Systeme.io tunnels de vente
const CHECKOUT_SUBSCRIPTION_URL = process.env.REACT_APP_CHECKOUT_SUBSCRIPTION_URL || 'https://capsulepatrimoine.systeme.io/abonnement';

const ACTIVITES = [
  { value: 'BIC_VENTE', label: 'Vente de marchandises', desc: 'Achat-revente, e-commerce, artisanat de production', icon: Building2, taux_urssaf: 12.3, taux_vl: 1.0, taux_cfp: 0.1, plafond: 203100 },
  { value: 'BIC_SERVICE', label: 'Prestation de services (BIC)', desc: 'Coaching, conseils commerciaux, services', icon: Briefcase, taux_urssaf: 21.2, taux_vl: 1.7, taux_cfp: 0.3, plafond: 83600 },
  { value: 'BNC', label: 'Profession libérale (BNC)', desc: 'Consultant, formateur, traducteur, rédacteur', icon: FileText, taux_urssaf: 25.6, taux_vl: 2.2, taux_cfp: 0.2, plafond: 83600 },
  { value: 'BNC_CIPAV', label: 'Profession libérale CIPAV', desc: 'Architecte, psychologue, ostéopathe, diététicien', icon: Stethoscope, taux_urssaf: 23.2, taux_vl: 2.2, taux_cfp: 0.2, plafond: 83600 }
];

const formatEuro = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const formatEuroPrecise = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);

const DemoPage = () => {
  const [searchParams] = useSearchParams();
  const initialStep = parseInt(searchParams.get('step') || '1', 10);
  const [step, setStep] = useState(Math.min(Math.max(initialStep, 1), 4));

  const [activite, setActivite] = useState('BIC_SERVICE');
  const [tvaSubject, setTvaSubject] = useState(false);
  const [versementLib, setVersementLib] = useState(false);
  const [caMensuel, setCaMensuel] = useState('');

  const [leadEmail, setLeadEmail] = useState('');
  const [leadPrenom, setLeadPrenom] = useState('');
  const [sendingLead, setSendingLead] = useState(false);
  const [leadSent, setLeadSent] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  const activiteData = ACTIVITES.find(a => a.value === activite) || ACTIVITES[1];
  const ca = parseFloat(caMensuel) || 0;
  const tauxUrssaf = activiteData.taux_urssaf;
  const tauxVL = versementLib ? activiteData.taux_vl : 0;
  const tauxCFP = activiteData.taux_cfp;

  const tvaCollectee = tvaSubject ? ca * 0.20 : 0;
  const baseCotisations = ca;
  const cotUrssaf = baseCotisations * (tauxUrssaf / 100);
  const cotVL = baseCotisations * (tauxVL / 100);
  const cotCFP = baseCotisations * (tauxCFP / 100);
  const cotTotal = cotUrssaf + cotVL + cotCFP;
  const revenuNet = baseCotisations - cotTotal;

  const caAnnuel = ca * 12;
  const netAnnuel = revenuNet * 12;
  const pctPlafond = activiteData.plafond > 0 ? Math.min((caAnnuel / activiteData.plafond) * 100, 100) : 0;

  const retraiteAE = Math.max(400, Math.min(700, ca * 0.18));
  const retraiteEI = Math.max(900, Math.min(1500, ca * 0.45));

  const canGoStep2 = ca > 0;

  // === Tracking ViewContent à l'étape 3 (signal d'intérêt fort) ===
  useEffect(() => {
    if (step === 3) {
      fbTrack('ViewContent', {
        content_name: 'Demo Dashboard Preview',
        content_category: 'Demo',
        value: revenuNet,
        currency: 'EUR'
      });
    }
  }, [step, revenuNet]);

  const handleSubmitLead = async () => {
    if (!leadEmail || !/\S+@\S+\.\S+/.test(leadEmail)) {
      toast.error('Email invalide');
      return;
    }
    setSendingLead(true);
    try {
      const res = await fetch(`${API_URL}/api/demo/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: leadEmail,
          prenom: leadPrenom,
          type_activite: activite,
          is_tva_subject: tvaSubject,
          versement_liberatoire: versementLib,
          ca_mensuel: ca,
          cotisations_total: cotTotal,
          revenu_net: revenuNet,
          tva_collectee: tvaCollectee,
          source: 'demo'
        })
      });
      if (!res.ok) throw new Error('Échec envoi');
      const respData = await res.json().catch(() => ({}));
      setLeadSent(true);
      // FB Pixel — Lead capturé (avec eventID pour déduplication avec la CAPI serveur)
      fbTrack('Lead', {
        content_name: 'Demo Lead Email',
        content_category: 'Demo',
        value: revenuNet,
        currency: 'EUR'
      }, respData.lead_event_id);
      toast.success('Vos résultats ont été envoyés par email');
    } catch (e) {
      toast.error('Erreur lors de l\'envoi. Réessayez.');
    } finally {
      setSendingLead(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/40 to-slate-100">
      <header className="sticky top-0 z-40 glass border-b border-emerald-100/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-bold text-lg sm:text-xl font-['Manrope']" data-testid="demo-home-link">
            <span className="text-emerald-600">Capsule</span> Patrimoine
          </Link>
          <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
            <Sparkles className="h-4 w-4 text-emerald-600" />
            <span className="hidden sm:inline">Outil démo gratuit</span>
            <span className="sm:hidden">Démo</span>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6">
        <div className="flex items-center justify-between gap-2 mb-2">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex-1 flex items-center" data-testid={`stepper-step-${s}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                step >= s ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200' : 'bg-white border-2 border-slate-200 text-slate-400'
              }`}>
                {step > s ? <CheckCircle className="h-4 w-4" /> : s}
              </div>
              {s < 4 && <div className={`flex-1 h-1 mx-2 rounded-full transition-all ${step > s ? 'bg-emerald-600' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-xs text-muted-foreground">
          <span className={step === 1 ? 'font-semibold text-emerald-700' : ''}>Activité</span>
          <span className={step === 2 ? 'font-semibold text-emerald-700' : ''}>Cotisations</span>
          <span className={step === 3 ? 'font-semibold text-emerald-700' : ''}>Dashboard</span>
          <span className={step === 4 ? 'font-semibold text-emerald-700' : ''}>Récap</span>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {step === 1 && (
          <div className="animate-fade-in space-y-6" data-testid="demo-step-1">
            <div className="text-center max-w-2xl mx-auto">
              <h1 className="text-3xl sm:text-4xl font-bold font-['Manrope'] mb-3">
                Estimez vos charges en <span className="text-emerald-600">moins d'1 minute</span>
              </h1>
              <p className="text-base text-muted-foreground">
                Combien vais-je vraiment toucher chaque mois ? Saisissez votre CA et obtenez vos résultats détaillés.
              </p>
            </div>

            <Card className="shadow-lg border-emerald-100">
              <CardContent className="p-6 sm:p-8 space-y-6">
                <div>
                  <Label className="text-base font-semibold mb-3 block">1. Quel est votre type d'activité ?</Label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {ACTIVITES.map((a) => {
                      const Icon = a.icon;
                      const selected = activite === a.value;
                      return (
                        <button
                          key={a.value}
                          type="button"
                          onClick={() => setActivite(a.value)}
                          data-testid={`activite-${a.value}`}
                          className={`text-left p-4 rounded-xl border-2 transition-all ${
                            selected
                              ? 'border-emerald-600 bg-emerald-50 shadow-md'
                              : 'border-slate-200 bg-white hover:border-emerald-300'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              selected ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-700'
                            }`}>
                              <Icon className="h-5 w-5" />
                            </div>
                            <div>
                              <p className="font-semibold text-sm">{a.label}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{a.desc}</p>
                              <p className="text-xs font-medium text-emerald-700 mt-1">URSSAF : {a.taux_urssaf}%</p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border">
                    <div>
                      <Label htmlFor="tva-switch" className="text-sm font-semibold cursor-pointer">Assujetti à la TVA ?</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">CA &gt; 37 500 € (services) ou 85 000 € (vente)</p>
                    </div>
                    <Switch id="tva-switch" checked={tvaSubject} onCheckedChange={setTvaSubject} data-testid="tva-switch" />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border">
                    <div>
                      <Label htmlFor="vl-switch" className="text-sm font-semibold cursor-pointer">Versement libératoire ?</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">Impôt sur le revenu prélevé à la source</p>
                    </div>
                    <Switch id="vl-switch" checked={versementLib} onCheckedChange={setVersementLib} data-testid="vl-switch" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="ca-input" className="text-base font-semibold mb-2 block">
                    2. Votre chiffre d'affaires {tvaSubject ? 'HT' : ''} mensuel
                  </Label>
                  <div className="relative">
                    <Input
                      id="ca-input"
                      type="number"
                      placeholder="Ex : 3500"
                      value={caMensuel}
                      onChange={(e) => setCaMensuel(e.target.value)}
                      className="text-2xl font-bold h-16 pl-6 pr-12 border-2 focus:border-emerald-600"
                      data-testid="ca-input"
                    />
                    <span className="absolute right-6 top-1/2 -translate-y-1/2 text-2xl font-bold text-muted-foreground">€</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Saisissez le montant {tvaSubject ? 'hors taxes' : 'encaissé'} que vous facturez en moyenne sur un mois
                  </p>
                </div>

                <Button
                  size="lg"
                  className="w-full h-14 text-base font-semibold bg-emerald-600 hover:bg-emerald-700"
                  disabled={!canGoStep2}
                  onClick={() => setStep(2)}
                  data-testid="step1-next-btn"
                >
                  Voir mes résultats
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </CardContent>
            </Card>

            <p className="text-center text-xs text-muted-foreground">
              100% gratuit · Aucune création de compte requise · Résultats instantanés
            </p>
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade-in space-y-6" data-testid="demo-step-2">
            <div className="text-center">
              <p className="text-sm font-medium text-emerald-700 mb-2">VOS RÉSULTATS</p>
              <h1 className="text-3xl sm:text-4xl font-bold font-['Manrope'] mb-2">
                Sur {formatEuro(ca)} de CA mensuel
              </h1>
              <p className="text-muted-foreground">{activiteData.label} {tvaSubject && '· Assujetti TVA'}</p>
            </div>

            <Card className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white shadow-2xl border-0 overflow-hidden">
              <CardContent className="p-8 text-center relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl"></div>
                <p className="text-emerald-100 text-sm uppercase tracking-wide mb-2">Votre revenu net mensuel estimé</p>
                <p className="text-5xl sm:text-6xl font-bold font-['Manrope'] mb-2" data-testid="revenu-net-display">
                  {formatEuroPrecise(revenuNet)}
                </p>
                <p className="text-emerald-100 text-sm">
                  Soit <strong className="text-white">{formatEuro(netAnnuel)}/an</strong> environ
                </p>
              </CardContent>
            </Card>

            <div className="grid sm:grid-cols-2 gap-4">
              <Card className="border-red-100">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-semibold text-sm">Cotisations URSSAF</p>
                    <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full font-medium">{tauxUrssaf}%</span>
                  </div>
                  <p className="text-2xl font-bold text-red-600">{formatEuroPrecise(cotUrssaf)}</p>
                  <p className="text-xs text-muted-foreground mt-1">par mois</p>
                </CardContent>
              </Card>
              <Card className="border-amber-100">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-semibold text-sm">CFP {versementLib ? '+ VL IR' : ''}</p>
                    <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full font-medium">{(tauxCFP + tauxVL).toFixed(1)}%</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-600">{formatEuroPrecise(cotVL + cotCFP)}</p>
                  <p className="text-xs text-muted-foreground mt-1">par mois</p>
                </CardContent>
              </Card>
              {tvaSubject && (
                <Card className="border-blue-100 sm:col-span-2">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-semibold text-sm">TVA collectée à reverser</p>
                        <p className="text-xs text-muted-foreground">Vous l'encaissez puis la reversez à l'État</p>
                      </div>
                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">20%</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">{formatEuroPrecise(tvaCollectee)}</p>
                  </CardContent>
                </Card>
              )}
            </div>

            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-semibold text-sm flex items-center gap-2">
                    <AlertTriangle className={`h-4 w-4 ${pctPlafond > 80 ? 'text-red-600' : 'text-emerald-600'}`} />
                    Plafond annuel ({formatEuro(activiteData.plafond)})
                  </p>
                  <p className="text-sm font-bold">{pctPlafond.toFixed(1)}%</p>
                </div>
                <Progress value={pctPlafond} className="h-2" />
                <p className="text-xs text-muted-foreground mt-2">
                  CA annuel projeté : <strong>{formatEuro(caAnnuel)}</strong> sur {formatEuro(activiteData.plafond)}
                </p>
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} data-testid="step2-back-btn">
                <ArrowLeft className="h-4 w-4 mr-1" /> Modifier
              </Button>
              <Button
                size="lg"
                className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700"
                onClick={() => setStep(3)}
                data-testid="step2-next-btn"
              >
                Voir l'aperçu du dashboard <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="animate-fade-in space-y-6" data-testid="demo-step-3">
            <div className="text-center">
              <p className="text-sm font-medium text-emerald-700 mb-2">APERÇU DU DASHBOARD</p>
              <h1 className="text-3xl sm:text-4xl font-bold font-['Manrope'] mb-2">
                Votre activité, <span className="text-emerald-600">en un coup d'œil</span>
              </h1>
              <p className="text-muted-foreground">Voici ce que vous voyez dans la version complète</p>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <Card className="card-hover border-emerald-100">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">CA encaissé</p>
                    <BarChart3 className="h-4 w-4 text-emerald-600" />
                  </div>
                  <p className="text-2xl font-bold text-emerald-700">{formatEuro(ca)}</p>
                  <p className="text-xs text-emerald-600 mt-1">+ ce mois-ci</p>
                </CardContent>
              </Card>
              <Card className="card-hover border-red-100">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">À provisionner</p>
                    <Calculator className="h-4 w-4 text-red-600" />
                  </div>
                  <p className="text-2xl font-bold text-red-600">{formatEuro(cotTotal + tvaCollectee)}</p>
                  <p className="text-xs text-muted-foreground mt-1">URSSAF {tvaSubject && '+ TVA'}</p>
                </CardContent>
              </Card>
              <Card className="card-hover border-blue-100">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Net disponible</p>
                    <PiggyBank className="h-4 w-4 text-blue-600" />
                  </div>
                  <p className="text-2xl font-bold text-blue-700">{formatEuro(revenuNet)}</p>
                  <p className="text-xs text-muted-foreground mt-1">après charges</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-semibold">Projection 12 mois</p>
                    <p className="text-xs text-muted-foreground">Si vous maintenez ce rythme</p>
                  </div>
                  <TrendingUp className="h-5 w-5 text-emerald-600" />
                </div>
                <div className="flex items-end gap-1 h-32 mb-3">
                  {Array.from({ length: 12 }).map((_, i) => {
                    const variation = 0.85 + (i % 3) * 0.08;
                    const height = (variation / 1.1) * 100;
                    return (
                      <div
                        key={`bar-${i}`}
                        className="flex-1 bg-gradient-to-t from-emerald-600 to-emerald-400 rounded-t transition-all hover:from-emerald-700"
                        style={{ height: `${height}%`, minHeight: '8px', animation: `slideIn 0.4s ease-out ${i * 0.05}s backwards` }}
                      />
                    );
                  })}
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>J</span><span>F</span><span>M</span><span>A</span><span>M</span><span>J</span>
                  <span>J</span><span>A</span><span>S</span><span>O</span><span>N</span><span>D</span>
                </div>
                <div className="mt-4 pt-4 border-t flex justify-between items-baseline">
                  <span className="text-sm text-muted-foreground">Net annuel projeté</span>
                  <span className="text-2xl font-bold text-emerald-600">{formatEuro(netAnnuel)}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-orange-200 bg-orange-50/50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Shield className="h-6 w-6 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold mb-2">Votre future retraite estimée</p>
                    <div className="grid sm:grid-cols-2 gap-3 mb-3">
                      <div className="bg-white rounded-lg p-3 border border-red-200">
                        <p className="text-xs text-muted-foreground">En auto-entrepreneur</p>
                        <p className="text-xl font-bold text-red-600">{formatEuro(retraiteAE)}/mois</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-emerald-200">
                        <p className="text-xs text-muted-foreground">En entreprise individuelle</p>
                        <p className="text-xl font-bold text-emerald-600">{formatEuro(retraiteEI)}/mois</p>
                      </div>
                    </div>
                    <p className="text-xs text-orange-800">
                      <strong>Différence : {formatEuro(retraiteEI - retraiteAE)}/mois</strong> — soit ~{formatEuro((retraiteEI - retraiteAE) * 12 * 20)} sur 20 ans de retraite
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-dashed border-2 border-emerald-200 bg-emerald-50/30">
              <CardContent className="p-6">
                <p className="font-semibold mb-4 flex items-center gap-2">
                  <Lock className="h-4 w-4 text-emerald-700" />
                  Et bien plus dans la version complète :
                </p>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  {[
                    { icon: FileText, label: 'Factures & devis en 2 clics' },
                    { icon: Calculator, label: 'Suivi des dépenses pro' },
                    { icon: TrendingUp, label: 'Simulateur passage EI/EURL' },
                    { icon: Shield, label: 'Protection sociale détaillée' },
                    { icon: PiggyBank, label: 'Plan retraite personnalisé' },
                    { icon: Gift, label: '9 guides experts inclus' }
                  ].map((f) => (
                    <div key={f.label} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-emerald-100">
                      <f.icon className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                      <span>{f.label}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(2)} data-testid="step3-back-btn">
                <ArrowLeft className="h-4 w-4 mr-1" /> Retour
              </Button>
              <Button
                size="lg"
                className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700"
                onClick={() => setStep(4)}
                data-testid="step3-next-btn"
              >
                Débloquer la version complète <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="animate-fade-in space-y-6" data-testid="demo-step-4">
            <div className="text-center max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
                <Star className="h-4 w-4 fill-emerald-600" />
                Offre lancement limitée
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold font-['Manrope'] mb-3">
                Prêt·e à reprendre le contrôle de votre activité ?
              </h1>
              <p className="text-muted-foreground">
                Choisissez la formule qui vous convient — accès immédiat après paiement.
              </p>
            </div>

            {!leadSent ? (
              <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                <CardContent className="p-5 sm:p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold mb-1">Vous voulez d'abord recevoir vos résultats par email ?</p>
                      <p className="text-sm text-muted-foreground mb-3">On vous envoie le récap complet de votre simulation.</p>
                      <div className="grid sm:grid-cols-3 gap-2">
                        <Input
                          placeholder="Prénom (optionnel)"
                          value={leadPrenom}
                          onChange={(e) => setLeadPrenom(e.target.value)}
                          data-testid="lead-prenom-input"
                        />
                        <Input
                          type="email"
                          placeholder="votre@email.com"
                          value={leadEmail}
                          onChange={(e) => setLeadEmail(e.target.value)}
                          data-testid="lead-email-input"
                        />
                        <Button
                          onClick={handleSubmitLead}
                          disabled={sendingLead}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid="lead-submit-btn"
                        >
                          {sendingLead ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Recevoir <Mail className="h-4 w-4 ml-1" /></>)}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="p-5 flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-emerald-600" />
                  <p className="text-sm font-medium text-emerald-900">
                    Email envoyé à <strong>{leadEmail}</strong>. Vérifiez aussi vos spams.
                  </p>
                </CardContent>
              </Card>
            )}

            <div className="grid md:grid-cols-2 gap-5">
              <Card className="border-2 border-blue-200 hover:border-blue-400 transition-all card-hover" data-testid="subscription-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Zap className="h-5 w-5 text-blue-600" />
                    </div>
                    <span className="text-xs font-semibold text-blue-700 uppercase">Abonnement</span>
                  </div>
                  <p className="font-bold text-lg mb-1">Mensuel sans engagement</p>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-4xl font-bold">29,99€</span>
                    <span className="text-muted-foreground">/mois</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-4">Sans engagement · résiliable à tout moment</p>

                  <ul className="space-y-2 text-sm mb-5">
                    {['Accès complet au dashboard', 'Tous les simulateurs', 'Mises à jour incluses', 'Support email'].map(item => (
                      <li key={item} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <a
                    href={CHECKOUT_SUBSCRIPTION_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                    data-testid="cta-subscription"
                    onClick={() => fbTrack('InitiateCheckout', {
                      content_name: 'Abonnement Mensuel',
                      content_category: 'Subscription',
                      value: 29.99,
                      currency: 'EUR'
                    })}
                  >
                    <Button size="lg" className="w-full h-12 bg-blue-600 hover:bg-blue-700">
                      Démarrer l'abonnement <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </a>
                  <p className="text-xs text-center text-muted-foreground mt-2">Paiement sécurisé via PayPal</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-emerald-500 hover:border-emerald-600 transition-all card-hover relative shadow-xl shadow-emerald-100" data-testid="annual-card">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                  MEILLEURE OFFRE
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Star className="h-5 w-5 text-emerald-600 fill-emerald-600" />
                    </div>
                    <span className="text-xs font-semibold text-emerald-700 uppercase">Abonnement annuel</span>
                  </div>
                  <p className="font-bold text-lg mb-1">Formule annuelle — 2 mois offerts</p>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-4xl font-bold text-emerald-600">299€</span>
                    <span className="text-muted-foreground">/an</span>
                    <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full whitespace-nowrap">≈ 24,92€/mois</span>
                  </div>
                  <p className="text-xs text-emerald-700 font-medium mb-4">Économisez 2 mois par rapport au mensuel</p>

                  <ul className="space-y-2 text-sm mb-5">
                    {[
                      'Tout du mensuel, sur 12 mois',
                      '9 guides experts inclus (993€ valeur)',
                      'Mises à jour incluses',
                      'Support prioritaire 3 mois'
                    ].map(item => (
                      <li key={item} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="font-medium">{item}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/achat"
                    className="block"
                    data-testid="cta-annual"
                    onClick={() => fbTrack('InitiateCheckout', {
                      content_name: 'Abonnement Annuel',
                      content_category: 'Annual',
                      value: 299,
                      currency: 'EUR'
                    })}
                  >
                    <Button size="lg" className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 font-bold">
                      S&apos;abonner à l&apos;année — 299€ <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                  <p className="text-xs text-center text-muted-foreground mt-2">Garantie 14 jours satisfait ou remboursé</p>
                </CardContent>
              </Card>
            </div>

            <div className="text-center pt-2">
              <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground flex-wrap">
                <span className="flex items-center gap-1"><Shield className="h-3 w-3 text-emerald-600" /> Paiement sécurisé PayPal</span>
                <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-emerald-600" /> Accès immédiat</span>
                <span className="flex items-center gap-1"><Mail className="h-3 w-3 text-emerald-600" /> Identifiants envoyés par email</span>
              </div>
            </div>

            <div className="text-center pt-4">
              <button onClick={() => setStep(1)} className="text-sm text-muted-foreground hover:text-emerald-700 underline" data-testid="step4-restart-btn">
                ← Refaire une simulation
              </button>
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-emerald-100/60 py-6 mt-8">
        <div className="max-w-5xl mx-auto px-4 text-center text-xs text-muted-foreground">
          <p className="mb-2">
            <Link to="/conformite-2026" className="text-blue-700 hover:underline font-medium inline-flex items-center gap-1" data-testid="footer-link-conformite">
              🛡️ Anticipez la réforme facturation électronique 2026
            </Link>
          </p>
          © 2026 Capsule Patrimoine — SIRET 484 280 771 00060 ·{' '}
          <a href="mailto:contact@capsulepatrimoine.com" className="hover:text-emerald-700">contact@capsulepatrimoine.com</a>
        </div>
      </footer>
    </div>
  );
};

export default DemoPage;
